import {
    CUSTOM_SOCIAL_LOGIN_KEYS,
    isCustomSocialLoginPlatform,
    LOGIN_METHOD_TO_THIRD_PARTY_PLATFORM
} from 'config/thirdParty';
import { isArray } from 'lodash';
import { isEmptyPlanItems } from 'utils/plan';
import { parseJSON_ifParsable } from './objectManipulate';

/**
 * 取得合作夥伴和贊助者資料
 * @param {Object} param - 參數物件
 * @param {Array<string>} [param.key=[]] - 要檢索的鍵
 * @param {Object} [param.sponsor={}] - sponsor 資料
 * @param {Object} [param.partner={}] - partner 資料
 * @returns {Object} - 返回帶有檢索鍵值的物件
 */
export const retrievePartnerSponsorData = ({ key = [], sponsor = {}, partner = {} }) => {
    if (!key.length) return {};

    return key.reduce((acc, currentKey) => {
        acc[currentKey] = sponsor[currentKey] ?? partner[currentKey];
        return acc;
    }, {});
};

/**
 * 從 third party 資料提取自訂社群登入設定資料
 * @param {Object} param - 參數物件
 * @param {Object} [param.thirdPartyData={}] - 第三方資料
 * @param {string} param.platform - 平台
 * @returns {Object} - 自訂社群登入設定
 */
const generateCustomSocialLoginConfig = ({ thirdPartyData = {}, platform }) => {
    const loginKeys = CUSTOM_SOCIAL_LOGIN_KEYS[platform];
    // 沒有 key 代表不屬於自訂社群登入平台
    if (!loginKeys?.length) return {};

    return loginKeys.reduce((acc, key) => {
        acc[key] = thirdPartyData[key] ?? '';
        return acc;
    }, {});
};

/**
 * 將 sponsor 與 partner 的第三方設定資料合併
 * @param {Object} sponsorSetting - Sponsor 設定
 * @param {Object} partnerSetting - Partner 設定
 * @param {boolean} isComparePartner - 是否要比較 Partner
 * @returns {Object} - 合併後的設定
 */
const mergeThirdPartySettings = (sponsorSetting = {}, partnerSetting = {}, isComparePartner) => {
    if (!isComparePartner) return sponsorSetting;

    return {
        ...partnerSetting,
        ...sponsorSetting
    };
};

/**
 * 取得自訂社群登入設定資料
 * @param {Object} param - 參數物件
 * @param {Object} [param.sponsor={}] - sponsor 資料
 * @param {Object} [param.partner={}] - partner 資料
 * @param {Object} [param.planItems={}] - 方案資料
 * @param {string} param.platform - 平台
 * @param {boolean} [param.isComparePartner=true] - 是否要取得 Partner 資料
 * @param {boolean} [param.isCheckCustomSocialLogin=true] - 是否要檢查自訂社群登入
 * @returns {Object} - 登入設定
 */
export const retrieveCustomSocialLoginConfig = ({
    sponsor = {},
    partner = {},
    planItems = {},
    platform,
    isComparePartner = true
}) => {
    const sponsorThirdPartySetting = sponsor.thirdParty?.[platform] ?? {};
    const partnerThirdPartySetting = partner.thirdParty?.[platform] ?? {};

    if (!platform || isEmptyPlanItems(planItems) || !CUSTOM_SOCIAL_LOGIN_KEYS[platform]) {
        // 沒有任何方案（新客戶可能在尚未有方案的情況下使用) or 不是自訂社群登入平台直接 sponsor 覆蓋 partner
        return generateCustomSocialLoginConfig({
            thirdPartyData: mergeThirdPartySettings(
                sponsorThirdPartySetting,
                partnerThirdPartySetting,
                isComparePartner
            ),
            platform
        });
    }

    // 取得自訂社群登入限制的平台資料
    const { customSocialLogin } = planItems;
    const parsedCustomSocialLogin = parseJSON_ifParsable(customSocialLogin, []);

    // 檢查是否為自訂社群登入限制平台
    const hasCustomSocialLogin = parsedCustomSocialLogin.some(
        loginMethod => LOGIN_METHOD_TO_THIRD_PARTY_PLATFORM[loginMethod] === platform
    );

    if (hasCustomSocialLogin) {
        // 自訂社群登入限制平台直接使用 Sponsor 資料
        return generateCustomSocialLoginConfig({
            thirdPartyData: sponsorThirdPartySetting,
            platform
        });
    } else {
        // 自訂社群登入非限制平台，使用 Partner 資料，若 isComparePartner 設定為 false 就直接煩回空物件
        return generateCustomSocialLoginConfig({
            thirdPartyData: isComparePartner ? partnerThirdPartySetting : {},
            platform
        });
    }
};

/**
 * 取得 ThirdParty 資料
 * @param {Object} param - 參數物件
 * @param {Object} [param.partner={}] - partner 資料
 * @param {Object} [param.sponsor={}] - sponsor 資料
 * @param {Object} [param.planItems={}] - 方案資料
 * @param {string|Array<string>} [param.platform] - 搜尋的平台
 * @param {boolean} [param.isComparePartner=true] - 是否要取得 Partner 資料
 * @param {boolean} [param.isCheckCustomSocialLogin=true] - 是否要檢查自訂社群登入
 * @returns {Object} - 包含對應平台在 ThirdParty 中資料的物件
 */
export const retrieveThirdPartyData = ({
    partner = {},
    sponsor = {},
    planItems = {},
    platform,
    isComparePartner = true,
    isCheckCustomSocialLogin = true
}) => {
    let currentPlatform = platform;
    const sponsorThirdParty = sponsor.thirdParty ?? {};
    const partnerThirdParty = partner.thirdParty ?? {};

    if (!platform || (isArray(platform) && platform.length === 0)) {
        // 取得 sponsorThirdParty 與 partnerThirdParty 的所有不重複 key
        currentPlatform = [
            ...new Set([...Object.keys(sponsorThirdParty), ...Object.keys(partnerThirdParty)])
        ];
    }

    const generateSettings = currentPlatform => {
        let result = mergeThirdPartySettings(
            sponsorThirdParty[currentPlatform] ?? {},
            partnerThirdParty[currentPlatform] ?? {},
            isComparePartner
        );

        // 處理自訂社群登入平台
        if (isCustomSocialLoginPlatform(currentPlatform) && isCheckCustomSocialLogin) {
            // 取得自訂社群登入設定資料
            const customSocialLoginConfig = retrieveCustomSocialLoginConfig({
                sponsor,
                partner,
                planItems,
                platform: currentPlatform,
                isComparePartner,
                isCheckCustomSocialLogin
            });

            result = { ...result, ...customSocialLoginConfig };
        }

        return result;
    };

    if (isArray(currentPlatform)) {
        if (currentPlatform.length === 1) return generateSettings(currentPlatform[0]);

        return currentPlatform.reduce((acc, currentPlatform) => {
            acc[currentPlatform] = generateSettings(currentPlatform);
            return acc;
        }, {});
    }

    return generateSettings(currentPlatform);
};
